import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { CreateVehicleDto, VehicleResponseDto, VehicleService } from '../../services/vehicle.service';
import { VehicleFormFieldsComponent } from '../vehicle-form-fields/vehicle-form-fields.component';
import { buildVehicleForm, vehicleFormValue } from '../vehicle-form-fields/vehicle-form';
import { AlertService } from '../../services/alert.service';
import { StatefulComponent } from '../stateful/stateful.component';
import { switchMap, catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { UserSearchTypeaheadComponent } from '../user-search-typeahead/user-search-typeahead.component';
import { UserDetail } from '../../models/UserDetail.model';

@Component({
  selector: 'app-add-vehicle-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, VehicleFormFieldsComponent, UserSearchTypeaheadComponent, CdkScrollable],
  templateUrl: './add-vehicle-modal.component.html',
  styleUrls: ['./add-vehicle-modal.component.scss']
})
export class AddVehicleModalComponent extends StatefulComponent implements OnInit {
  @Input() modalRef: NgbModalRef | undefined;
  // When set, the vehicle is created for this customer and the owner-selection
  // UI is hidden (e.g. the create-quote flow already knows the customer).
  @Input() presetCustUserId: string | null = null;
  @Output() vehicleAdded = new EventEmitter<VehicleResponseDto>();

  vehicleForm: FormGroup = new FormGroup({});
  selectedFile: File | null = null;
  imagePreview: string | null = null;
  isSubmitting: boolean = false;

  // User selection properties
  vehicleIsForMe: boolean = false;
  selectedUser: UserDetail | null = null;

  constructor(
    private vehicleService: VehicleService,
    private alertService: AlertService
  ) {
    super();
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  get hasEditAllGaragesPermission(): boolean {
    return this.permissions?.includes('EDIT_ALL_GARAGES') || false;
  }

  get showOwnerSelection(): boolean {
    return this.hasEditAllGaragesPermission && !this.presetCustUserId;
  }

  initializeForm(): void {
    this.vehicleForm = buildVehicleForm();
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files[0]) {
      const file = input.files[0];

      // Validate the file
      const validation = this.vehicleService.validateImageFile(file);
      if (!validation.valid) {
        this.alertService.showSnackBar(validation.error || 'Invalid file');
        return;
      }

      this.selectedFile = file;
      this.imagePreview = this.vehicleService.createImagePreview(file);
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;
    if (files && files[0]) {
      const file = files[0];

      // Validate the file
      const validation = this.vehicleService.validateImageFile(file);
      if (!validation.valid) {
        this.alertService.showSnackBar(validation.error || 'Invalid file');
        return;
      }

      this.selectedFile = file;
      this.imagePreview = this.vehicleService.createImagePreview(file);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  removeImage(): void {
    if (this.imagePreview) {
      this.vehicleService.revokeImagePreview(this.imagePreview);
    }
    this.selectedFile = null;
    this.imagePreview = null;

    // Clear file input
    const fileInput = document.getElementById('vehicle-image-upload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  onUserSelected(user: UserDetail): void {
    this.selectedUser = user;
  }

  onVehicleIsForMeChange(): void {
    if (this.vehicleIsForMe) {
      // Clear selected user when checkbox is checked
      this.selectedUser = null;
    }
  }

  onSubmit(): void {
    // Validate form
    if (!this.vehicleForm.valid) {
      Object.keys(this.vehicleForm.controls).forEach(key => {
        const control = this.vehicleForm.get(key);
        if (control && !control.valid) {
          control.markAsDirty();
        }
      });
      this.alertService.showSnackBar('Please fill in all required fields correctly');
      return;
    }

    // Validate user selection if user has EDIT_ALL_GARAGES permission
    if (this.showOwnerSelection && !this.vehicleIsForMe && !this.selectedUser) {
      this.alertService.showSnackBar('Please select a user or check "Vehicle is for me"');
      return;
    }

    this.isSubmitting = true;

    // Determine the CustUserId based on selection
    let custUserId: string;
    if (this.presetCustUserId) {
      custUserId = this.presetCustUserId;
    } else if (this.hasEditAllGaragesPermission && !this.vehicleIsForMe && this.selectedUser) {
      custUserId = this.selectedUser.userId;
    } else {
      custUserId = this.user?.userId || '';
    }

    // Create vehicle DTO
    const vehicleData: CreateVehicleDto = {
      CustUserId: custUserId,
      ...vehicleFormValue(this.vehicleForm)
    };

    // Create vehicle and optionally upload image in a single pipe. Hold on to
    // the created vehicle so it can be emitted to listeners on success.
    let createdVehicle: VehicleResponseDto;
    this.subs.push(
      this.vehicleService.createVehicle(vehicleData).pipe(
        switchMap(vehicle => {
          createdVehicle = vehicle;
          // If there's an image, upload it with isPrimary set to true
          if (this.selectedFile) {
            return this.vehicleService.uploadVehicleImage(vehicle.VehicleId, this.selectedFile, true).pipe(
              catchError(error => {
                console.error('Image upload failed:', error);
                this.alertService.showSnackBar('Vehicle created, but image upload failed');
                return of(null); // Continue despite image upload failure
              })
            );
          }
          // No image to upload, return empty observable
          return of(null);
        }),
        catchError(error => {
          console.error('Vehicle creation failed:', error);
          this.alertService.showSnackBar('Failed to create vehicle. Please try again.');
          throw error; // Re-throw to prevent success handler from running
        }),
        finalize(() => {
          this.isSubmitting = false;
        })
      ).subscribe({
        next: () => {
          this.onSuccess(createdVehicle);
        },
        error: () => {
          // Error already handled in catchError
        }
      })
    );
  }

  onSuccess(createdVehicle: VehicleResponseDto): void {
    this.alertService.showSnackBar('Vehicle added successfully!');
    this.vehicleAdded.emit(createdVehicle);
    if (this.modalRef) {
      this.modalRef.close();
    }
  }

  onCancel(): void {
    if (this.imagePreview) {
      this.vehicleService.revokeImagePreview(this.imagePreview);
    }
    if (this.modalRef) {
      this.modalRef.dismiss();
    }
  }
}
